import { type CSSProperties, type HTMLAttributes } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { CustomFieldDefinition } from '../../orders/api/intakeApi'
import { newWorkingField, type WorkingField } from '../utils/workingFields'
import { FieldEditor, type FieldEditorProps } from './FieldEditor'
import { Button } from '../../../components/ui/button'
import { Plus } from 'lucide-react'

/** Sortable wrapper: owns the dnd-kit node + passes the drag handle into FieldEditor. */
function SortableFieldEditor({ uid, ...props }: { uid: string } & Omit<FieldEditorProps, 'dragHandleProps'>) {
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({ id: uid })
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
    position: 'relative',
  }
  return (
    <div ref={setNodeRef} style={style}>
      <FieldEditor {...props} dragHandleProps={{ ...attributes, ...listeners } as HTMLAttributes<HTMLButtonElement>} />
    </div>
  )
}

interface FieldSetEditorProps {
  fields: WorkingField[]
  onChange: (fields: WorkingField[]) => void
  /** Copy for the empty state. */
  emptyLabel?: string
  addLabel?: string
}

/**
 * A drag-and-drop list of full CustomFieldDefinition editors. Used both for the
 * solution TEMPLATE (TCS-admin tab) and for a merchant's own field list.
 * Pure controlled component — the parent owns persistence + validation.
 */
export function FieldSetEditor({
  fields,
  onChange,
  emptyLabel = 'No fields yet.',
  addLabel = 'Add Field',
}: FieldSetEditorProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const updateField = (idx: number, updated: CustomFieldDefinition) => {
    onChange(fields.map((f, i) => (i === idx ? { ...updated, __uid: f.__uid } : f)))
  }

  const removeField = (idx: number) => {
    onChange(fields.filter((_, i) => i !== idx))
  }

  const addField = () => {
    onChange([...fields, newWorkingField(fields.length)])
  }

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e
    if (!over || active.id === over.id) return
    const oldIndex = fields.findIndex((f) => f.__uid === active.id)
    const newIndex = fields.findIndex((f) => f.__uid === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    onChange(arrayMove(fields, oldIndex, newIndex))
  }

  if (fields.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border py-8 text-center">
        <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={addField}>
          <Plus className="h-4 w-4 mr-1" />
          {addLabel}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((f) => f.__uid)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {fields.map((field, idx) => (
              <SortableFieldEditor
                key={field.__uid}
                uid={field.__uid}
                field={field}
                index={idx}
                onChange={(updated) => updateField(idx, updated)}
                onRemove={() => removeField(idx)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <Button variant="outline" size="sm" onClick={addField}>
        <Plus className="h-4 w-4 mr-1" />
        {addLabel}
      </Button>
    </div>
  )
}
